import { Button, Col, Divider, Form, Input, Row, Table } from "antd";
import { FormLayout } from "antd/lib/form/Form";
import { FC, useEffect } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";

import styles from "./OutletManagementPage.module.css";

import { Head } from "components/molecules/Head/Head";
import PageTitle from "components/molecules/PageTitle/PageTitle";
import ViewEdit from "components/atoms/ViewEdit/ViewEdit";
import Label from "components/atoms/Label/Label";
import { FIELD_CONFIG, FORM_CONFIG } from "../config";
import { PageTitles, Routes as ROUTES } from "constants/global";
import getIcon from "utils/getIcon";
import { getLastUpdatedOutletList, getLastUpdatedOutletListSelector } from "redux/userList/userSlice";
import { useAppDispatch, useAppSelector } from "app/hooks/hooks";

const columnHeaders = [
	{
		title: "Last modified date and time",
		dataIndex: "lastModifiedDateAndTime",
		key: "lastModifiedDateAndTime"
	},
	{
		title: "Outlet name",
		dataIndex: "outletName",
		key: "outletName"
	},
	{
		title: "Outlet code",
		dataIndex: "outletId",
		key: "outletId"
	},
	{
		title: "SM Store ID",
		dataIndex: "smStoreId",
		key: "smStoreId"
	},
	{
		title: "View/ edit",
		dataIndex: "viewEdit",
		key: "viewEdit"
	}
];
const { validateMessages, layout, validateTrigger } = FORM_CONFIG;

const OutletManagementPage: FC = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const dispatch = useAppDispatch();
	const { loading: isFetchingOutletList, outlets } = useAppSelector(getLastUpdatedOutletListSelector);

	const isNestedRoute = location.pathname !== ROUTES.OUTLET_MANAGEMENT;

	useEffect(() => {
		dispatch(getLastUpdatedOutletList());
	}, []);

	if (isNestedRoute) return <Outlet />;

	const onSubmit = async ({ outletCode }: { outletCode: string }) => {
		if (!outletCode) return;
		navigate(`edit-outlet/${outletCode}`);
	};

	return (
		<>
			<Head>{PageTitles.OUTLET_MANAGEMENT}</Head>
			<div className={styles.root}>
				<PageTitle showBackIcon={false} title="Outlet Management" subTitle="Create new outlets or enter an outlet code to view and edit outlet details." />
				<div className="p-8">
					<div className="mb-5">
						<h3 className={styles["section-header"]}>Create an outlet</h3>
					</div>
					<Button className={styles?.["btn-primary"]} type="primary">
						<Link to={ROUTES.CREATE_OUTLET}>Create Outlet</Link>
					</Button>

					<Divider className="border-grey-100" />

					<div className="mb-5">
						<h3 className={styles["section-header"]}>Search by outlet code</h3>
					</div>
					<Form layout={layout as FormLayout} validateTrigger={validateTrigger} validateMessages={validateMessages} onFinish={onSubmit}>
						<div className="mb-1">
							<Label label={FIELD_CONFIG["outletCode"].label} classes={{ label: styles?.["form-label"] }} />
						</div>
						<Row gutter={8}>
							<Col className="gutter-row" span={8}>
								<Form.Item name="outletCode" rules={[{ required: true }]} normalize={(value: string) => value.trim().toUpperCase()}>
									<Input className="p-3 rounded-lg" placeholder={FIELD_CONFIG["outletCode"].label} maxLength={20} data-testid="outletCode" />
								</Form.Item>
							</Col>

							<Col className="gutter-row">
								<Form.Item>
									<Button aria-label="search" className={styles["icon-button"]} htmlType="submit">
										{getIcon({ icon: "search", iconColor: "#ffffff", width: 20, height: 20 })}
									</Button>
								</Form.Item>
							</Col>
						</Row>
					</Form>

					<Divider className="border-grey-100" />

					<div className="mb-5">
						<h3 className={styles["section-header"]}>Latest updates</h3>
					</div>

					<div className="p-5 border border-solid border-grey-100">
						<Table
							loading={isFetchingOutletList === 1}
							pagination={false}
							dataSource={outlets.map(({ modified_on: lastModifiedDateAndTime, outlet: outletName, tenant_no: outletId, sm_store_id: smStoreId }) => {
								return {
									key: smStoreId,
									lastModifiedDateAndTime,
									outletName,
									outletId,
									smStoreId,
									viewEdit: <ViewEdit onClick={() => navigate(`edit-outlet/${outletId}`)} />
								};
							})}
							columns={columnHeaders}
							size="small"
						/>
					</div>
				</div>
			</div>
		</>
	);
};

export default OutletManagementPage;
